import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  saveClip as _saveClip,
  getClip as _getClip,
  listClips as _listClips,
  deleteClip as _deleteClip,
  deleteAllClips as _deleteAllClips,
  purgeExpiredClips,
} from "../lib/audioClipStore";

const CONSENT_KEY = "smartdream_audio_clips_consent";

const AudioClipsContext = createContext({
  consent: null, // null = non ancora deciso, true/false = scelto
  consentAt: null,
  grantConsent: () => {},
  revokeConsent: () => {},
  clips: [],
  reloadClips: async () => {},
  saveClip: async () => {},
  getClip: async () => null,
  deleteClip: async () => {},
  deleteAllClips: async () => {},
});

export function AudioClipsProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [consentAt, setConsentAt] = useState(null);
  const [clips, setClips] = useState([]);

  const reloadClips = useCallback(async () => {
    try {
      const all = await _listClips();
      all.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
      setClips(all);
    } catch {
      setClips([]);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setConsent(parsed.granted);
        setConsentAt(parsed.at);
      }
    } catch {
      /* noop */
    }
    // Pulizia automatica dopo 30 giorni (retention policy GDPR)
    purgeExpiredClips(30).catch(() => {});
    reloadClips();
  }, [reloadClips]);

  const grantConsent = useCallback(() => {
    const at = new Date().toISOString();
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ granted: true, at })
    );
    setConsent(true);
    setConsentAt(at);
  }, []);

  const revokeConsent = useCallback(async () => {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ granted: false, at: new Date().toISOString() })
    );
    setConsent(false);
    // Revoca → elimina tutte le clip locali esistenti
    await _deleteAllClips();
    setClips([]);
  }, []);

  const saveClip = useCallback(
    async (clip) => {
      if (consent !== true) return false;
      await _saveClip(clip);
      await reloadClips();
      return true;
    },
    [consent, reloadClips]
  );

  const deleteClip = useCallback(
    async (id) => {
      await _deleteClip(id);
      await reloadClips();
    },
    [reloadClips]
  );

  const deleteAllClips = useCallback(async () => {
    await _deleteAllClips();
    setClips([]);
  }, []);

  return (
    <AudioClipsContext.Provider
      value={{
        consent,
        consentAt,
        grantConsent,
        revokeConsent,
        clips,
        reloadClips,
        saveClip,
        getClip: _getClip,
        deleteClip,
        deleteAllClips,
      }}
    >
      {children}
    </AudioClipsContext.Provider>
  );
}

export function useAudioClips() {
  return useContext(AudioClipsContext);
}
